import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { localTzLabel } from "@/lib/format";
import { Reveal } from "@/components/site/Reveal";
import { MonthCalendar } from "./MonthCalendar";
import { TimeSlots } from "./TimeSlots";
import { RegistrationForm } from "./RegistrationForm";
import { Confirmation } from "./Confirmation";
import { trackEvent } from "@/lib/tracker";

const EMPTY = { full_name: "", email: "", whatsapp: "", country: "", budget: "", question: "", website: "" };

export function BookingSection() {
  const [config, setConfig] = useState({ duration: 30, horizon_days: 60 });
  const [date, setDate] = useState(null);
  const [slot, setSlot] = useState(null);
  const [step, setStep] = useState("pick");
  const [form, setForm] = useState(EMPTY);
  const [result, setResult] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    api.get("/availability/config").then((r) => setConfig(r.data)).catch(() => {});
  }, []);

  const pickDate = (d) => {
    setDate(d);
    setSlot(null);
  };

  const backToPick = () => {
    setSlot(null);
    setStep("pick");
    setRefreshKey((k) => k + 1);
  };

  const done = (data) => {
    trackEvent("booked");
    setResult(data);
    setStep("done");
    setForm(EMPTY);
    setRefreshKey((k) => k + 1);
  };

  const reset = () => {
    setResult(null);
    setDate(null);
    setSlot(null);
    setStep("pick");
  };

  return (
    <section id="booking" className="relative py-24 sm:py-32 px-4 sm:px-10 overflow-hidden" data-testid="booking-section">
      <div className="pointer-events-none absolute top-20 left-[-8%] h-[420px] w-[420px] rounded-full bg-[#C7A76C]/15 blur-3xl" />
      <div className="pointer-events-none absolute bottom-10 right-[-8%] h-[460px] w-[460px] rounded-full bg-[#0B1F3A]/10 blur-3xl" />
      <div className="relative max-w-6xl mx-auto">
        <Reveal className="px-2">
          <p className="eyebrow">Book a briefing</p>
          <div className="mt-4 flex flex-col md:flex-row md:items-end md:justify-between gap-4">
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0B1F3A]">Choose your briefing time.</h2>
            <p className="text-sm text-[#697386]" data-testid="booking-timezone-label">
              Times shown in <span className="font-semibold text-[#172033]">{localTzLabel()}</span> · {config.duration} min
            </p>
          </div>
        </Reveal>

        <Reveal delay={0.08}>
          <div className="mt-10 glass rounded-[32px] p-4 sm:p-8">
            {step === "pick" && (
              <div className="grid lg:grid-cols-12 gap-6 lg:gap-10">
                <div className="lg:col-span-7">
                  <MonthCalendar selected={date} onSelect={pickDate} horizonDays={config.horizon_days} refreshKey={refreshKey} />
                </div>
                <div className="lg:col-span-5 lg:border-l border-[rgba(20,35,55,0.10)] lg:pl-10">
                  <TimeSlots date={date} selected={slot} onSelect={setSlot} refreshKey={refreshKey} onContinue={() => { trackEvent("booking_started"); setStep("form"); }} />
                </div>
              </div>
            )}
            {step === "form" && slot && (
              <RegistrationForm form={form} setForm={setForm} slot={slot} duration={config.duration} onBack={() => setStep("pick")} onPickAnother={backToPick} onSuccess={done} />
            )}
            {step === "done" && result && <Confirmation result={result} onReset={reset} />}
          </div>
        </Reveal>
      </div>
    </section>
  );
}
